import { CARET_OWNER_SELECTOR, ISSUE_REPORT_SELECTOR } from '../constants';

/**
 * The one way to put the caret back in the utterance input.
 *
 * The omnibar claims focus ONCE, when its input mounts, and is handed it back
 * explicitly afterwards — it does not grab it on every render (see the ⚠️ block on
 * `focusOmniBarInput`). Three callers hand it back:
 *
 *  - `useOmniBarFocusHandback` — after any click that did not land somewhere with a
 *    better claim. This is what keeps the TTS fast-path alive: Enter and the arrow
 *    keys are bound to the input, so an agent who clicked a pill or a cart item has
 *    no working shortcuts until the caret comes home.
 *  - `IssueFlagPanel` — when the flag window closes.
 *  - `AutocompleteComp` — when the input goes from disabled back to enabled, since
 *    the browser blurs a disabled input and the node never unmounts, so neither the
 *    mount ref nor `autoFocus` fires again.
 *
 * ⚠️ NODE, NOT ID. An earlier version looked the input up as `#omnitext`, the id set
 * on the omnibar's `TextField`. That id never reaches the DOM: MUI `Autocomplete`
 * supplies its own through `params.inputProps`, which is applied directly to the
 * input and beats the `id` prop, so the element is really `#mui-1`. The lookup
 * returned null and the handback silently did nothing. The node is registered here by
 * the same callback ref that focuses it on mount, so it cannot drift again.
 */
let omniBarInput: HTMLInputElement | null = null;

export const setOmniBarInput = (input: HTMLInputElement | null) => {
  omniBarInput = input;
};

/**
 * Input types that take no typed text, so focusing them is a selection gesture rather
 * than "the agent is writing something here".
 *
 * This distinction is not academic: MUI `Radio` and `Checkbox` render real
 * `<input type="radio">` / `<input type="checkbox">`, and they are what the ItemBuilder
 * modifier picker is made of. Treating every INPUT as text entry would mean that
 * clicking a combo size — the exact flow agents reported — left the caret on that radio
 * and the TTS fast-path dead, which is the bug this file exists to fix.
 *
 * A DOM `input.type` normalises anything it does not recognise to `text`, so an
 * unknown type errs toward being protected.
 */
const NON_TEXT_INPUT_TYPES = [
  'button',
  'checkbox',
  'color',
  'file',
  'image',
  'radio',
  'range',
  'reset',
  'submit',
];

const isTextEntry = (element: HTMLElement) => {
  if (element.isContentEditable) return true;
  const tag = element.tagName;
  // A focused <select> takes the arrow keys to change its value, so it keeps them.
  if (tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (tag !== 'INPUT') return false;
  const type = (element as HTMLInputElement).type?.toLowerCase();
  return !NON_TEXT_INPUT_TYPES.includes(type);
};

/**
 * Whether something on screen has a better claim to the caret than the omnibar.
 *
 * Deliberately shared by every path that focuses the omnibar — `focusOmniBar` and the
 * mount-time `focusOmniBarInput` — rather than repeated per caller. Both production
 * focus bugs in v6.3.0 came from one path having a guard the other did not, and the
 * caller list above is only going to grow.
 *
 * `closest()` throughout, so a marker need only sit on an ANCESTOR of the real target
 * — the same contract as the interception allowlist.
 */
export const somethingElseOwnsTheCaret = () => {
  if (typeof document === 'undefined') return false;
  // A modal owns the caret while it is up — never fight its focus trap. Pulling
  // against MUI's TrapFocus loops: it moves focus in, we move it out, and the omnibar
  // looks focused while every keystroke is swallowed.
  if (document.querySelector('[role="dialog"]')) return true;
  // A surface that declared itself the owner — the open flag panel. See
  // CARET_OWNER_SELECTOR for why this cannot be inferred from activeElement.
  if (document.querySelector(CARET_OWNER_SELECTOR)) return true;
  const active = document.activeElement as HTMLElement | null;
  if (!active) return false;
  if (active.closest(ISSUE_REPORT_SELECTOR)) return true;
  // Any other field the agent is typing in. If it IS the omnibar there is nothing to
  // do either way, so this needs no special case for it.
  return isTextEntry(active);
};

export const focusOmniBar = () => {
  if (somethingElseOwnsTheCaret()) return;
  // Guard the node too: a stale one left over from an unmounted layout must not be
  // focused.
  if (omniBarInput && document.contains(omniBarInput)) {
    omniBarInput.focus();
  }
};

/** Test seam — clears the module-level node between cases. */
export const resetOmniBarInput = () => {
  omniBarInput = null;
};

export default focusOmniBar;
